import type { FormTheme } from './forms';
import type { SectionDesign } from './section-design';
import type { FormFieldDesign } from './form-fields';
export type { SectionDesign } from './section-design';

// Full-page visual presets. The CURATED set (shown in the Style picker) maps each
// look to a real design language catalogued in Swipe: white (Clean White, flat
// Swiss/Apple restraint), linear (Linear, dark tech), corporate (Stripe, clean
// corporate), editorial (Aesop, editorial luxury), brutalist (Gumroad, bold
// type). glass (Spline, neon glass) and playful (Oatly, warm playful) are
// DEMOTED from the picker but still render in styles.ts for existing pages. The
// trailing keys are LEGACY: pre-consolidation styles kept byte-identical in
// styles.ts so old pages + section band tones are unchanged.
export type PageStyle =
  | 'white' | 'linear' | 'glass' | 'corporate' | 'editorial' | 'playful' | 'brutalist'
  | 'light' | 'dark' | 'bold' | 'cream' | 'midnight' | 'ocean' | 'slate' | 'gradient' | 'pastel' | 'aurora' | 'neon'
  // v2.58 modern funnel styles (flat surfaces, page-relative band tones; see styles.ts MODERN).
  | 'clean' | 'letter' | 'navy' | 'paper' | 'mono' | 'program' | 'atelier' | 'impact' | 'sunny';

// A curated display+body font pairing applied to the whole page (see fonts.ts
// FONTS). Absent = the app's default type (every old page is byte-identical).
export type PageFont = 'jakarta' | 'inter' | 'manrope' | 'sora' | 'space' | 'bricolage' | 'figtree' | 'playfair' | 'fraunces' | 'cormorant'
  | 'poppins' | 'oswald' | 'instrument' | 'outfit' | 'lato';

// A one-click premium look = a cohesive {style + font + brand} preset (see
// archetypes.ts ARCHETYPES), each modeled on a Swipe design reference. Stored only
// as a label of which preset was applied; rendering reads the underlying
// style/font/brand, so it needs no migration. Trailing keys are legacy presets.
export type Archetype =
  | 'course-launch' | 'lead-magnet' | 'sales-letter' | 'challenge-webinar' | 'vsl-call' | 'book-offer'
  | 'free-trial' | 'dark-program' | 'editorial-creator' | 'warm-course' | 'bold-coaching'
  // earlier presets: still valid labels on existing pages
  | 'immersive' | 'swiss' | 'premium-dark' | 'photo-funnel' | 'quiz-funnel'
  | 'neon-glass' | 'clean-corporate' | 'editorial-luxury' | 'warm-playful' | 'bold-type'
  | 'corporate-blue' | 'long-form' | 'dark-neon';
/** Every known section type. Single source of truth — SectionType derives from it
 *  and tests validate starter templates against it. */
export const SECTION_TYPES = [
  'hero', 'text', 'benefits', 'testimonial', 'image', 'embed', 'cta', 'form',
  'stats', 'features', 'faq', 'steps', 'logos', 'footer', 'booking',
  'pricing', 'video', 'gallery', 'team', 'guarantee', 'divider', 'countdown', 'split', 'compare',
  // Funnel blocks (v2.58): a top nav bar, an announcement strip, a review wall,
  // a curriculum, a creator bio and an offer box. Additive: no existing page uses them.
  'nav', 'banner', 'testimonials', 'modules', 'bio', 'offer',
] as const;
export type SectionType = (typeof SECTION_TYPES)[number];

/** Human label per section type. Used by the editor's section list, the
 *  add-section picker, and the visual canvas hover/selection tags. */
export const SECTION_LABEL: Record<SectionType, string> = {
  hero: 'Hero', text: 'Text', benefits: 'Benefits', testimonial: 'Testimonial',
  image: 'Image', embed: 'Embed', cta: 'CTA button', form: 'Form',
  stats: 'Stats bar', features: 'Feature grid', faq: 'FAQ', steps: 'Steps', logos: 'Logo strip',
  footer: 'Footer', booking: 'Booking calendar',
  pricing: 'Pricing', video: 'Video', gallery: 'Gallery', team: 'Team',
  guarantee: 'Guarantee', divider: 'Divider', countdown: 'Countdown', split: 'Image + text',
  compare: 'Comparison',
  nav: 'Top bar', banner: 'Announcement', testimonials: 'Reviews', modules: 'Modules',
  bio: 'Bio', offer: 'Offer',
};

export interface StatItem { value: string; label: string }
/** `icon_name` picks a built-in line icon (see components/pages/sections/icons);
 *  it wins over `image` and `icon` (emoji / short text) when set. */
export interface FeatureItem { icon?: string; icon_name?: string; image?: string; title: string; body?: string }
export interface FaqItem { q: string; a: string }
export interface StepItem { label: string; detail?: string }
export interface FooterLink { label: string; href: string }
export interface PricingPlan {
  name: string; price: string; period?: string; features: string[]; cta_label?: string; cta_href?: string; highlight?: boolean;
  /** Optional extras (absent = today's card): a one-line description under the
   *  name, a struck-through "was" price, and a custom ribbon label. */
  description?: string; price_compare?: string; badge?: string;
}
export interface TeamMember { photo_url?: string; name: string; role?: string }
/** One review in a `testimonials` wall. `rating` 1-5 renders stars. */
export interface QuoteItem {
  quote: string; name: string; role?: string; photo_url?: string; rating?: number;
  /** Optional media on top of the card: a screenshot / still (`image`), or a
   *  playable video when `video_url` is set too (image = its poster). */
  image?: string; video_url?: string;
}
/** One module of a `modules` curriculum. `label` is the small tag above the
 *  title (e.g. "Module 1", "Week 2", "Day 3"); absent = a plain number. */
export interface ModuleItem { label?: string; title: string; body?: string; lessons?: string[]; image?: string }
/** One line of an `offer` value stack. `value` is the optional "$297 value" note. */
export interface OfferItem { label: string; value?: string }

/** How a section's `highlight` words render. Absent = the page style's default. */
export type MarkStyle = 'color' | 'marker' | 'box' | 'italic' | 'underline';
export const MARK_STYLES: readonly MarkStyle[] = ['color', 'marker', 'box', 'italic', 'underline'];

/** Layout variant for the types that offer one. Each type reads only the values
 *  it supports; absent = the type's original layout (old pages unchanged). */
export type SectionVariant =
  | 'cards' | 'plain' | 'divided' | 'two-col' | 'grid' | 'wall' | 'list' | 'timeline' | 'center' | 'media' | 'compact';

// Per-section background band. 'default' inherits the page style; the others render
// a full-width band in that tone (with matching text colors) so a page can alternate
// light / dark / brand sections. 'light' / 'dark' / 'bold' are the classic tones
// (legacy gradient surfaces, kept byte-identical); 'tint' / 'ink' / 'brand' are the
// flat modern tones, resolved against the page's own style (see styles.ts bandTone).
export type SectionBg = 'default' | 'light' | 'dark' | 'bold' | 'tint' | 'ink' | 'brand';

// Per-section text scale for the text-bearing types. Absent (or 'md') renders
// EXACTLY today's sizes — old pages and published bundles are untouched.
export type SectionTextSize = 'sm' | 'md' | 'lg' | 'xl';

// Scroll-entrance animation for a section. Absent = 'fade-up' (the behavior every
// existing page already has), so the field is backward-compatible by construction.
export type SectionAnimation = 'fade-up' | 'fade-in' | 'slide-left' | 'slide-right' | 'zoom' | 'none';
export const SECTION_ANIMATIONS: readonly SectionAnimation[] = ['fade-up', 'fade-in', 'slide-left', 'slide-right', 'zoom', 'none'];

// What a form section's successful submit does next (see afterSubmitAction).
// Absent = legacy default: next step normally, falling back to 'form' on the
// last step — every existing page is byte-identical.
export type AfterSubmitAction = 'next' | 'thank_you' | 'form';

export interface Section {
  id: string;
  type: SectionType;
  design?: SectionDesign;                     // optional scoped color, spacing, width and responsive grid overrides
  hidden?: boolean;                            // editor "Hide from page": kept in the page but not rendered anywhere (absent = shown, today's behavior)
  bg?: SectionBg;                              // full-width background band (any section type)
  bg_image_url?: string;                       // full-bleed background image/SVG (any section type)
  bg_overlay?: number;                         // 0-100 dark scrim over bg_image_url (default 45 when set)
  text_size?: SectionTextSize;                 // text scale (text-bearing types; absent = md = today's sizes)
  space?: 'tight' | 'normal' | 'loose';        // per-section vertical spacing override (absent = automatic adjacency rule)
  space_below?: 'tight' | 'normal' | 'loose'; // per-section gap BELOW (absent = auto). Combined with the next section's `space`: the larger of the two wins (see spacing.ts).
  animation?: SectionAnimation;                // scroll-entrance variant (absent = 'fade-up', today's behavior; 'none' = paint immediately, no observer)
  logo?: boolean; eyebrow?: string; headline?: string; subheadline?: string; image_url?: string; image_pos?: 'side' | 'below' | 'background'; video_url?: string; // hero (video_url renders a VSL-style player; image_pos 'background' = full-bleed bg)
  image_side?: 'left' | 'right';              // split (image+text) section: which side the image sits on (desktop)
  heading?: string; body?: string;            // text (body also = footer text)
  items?: string[]; align?: 'left' | 'center' | 'right'; // benefits (align = list alignment, default left)
  list_style?: 'check' | 'number';             // benefits: 'number' renders numbered chips instead of checkmarks (absent/'check' = today's checkmarks, unchanged)
  left_title?: string; left_items?: string[]; right_title?: string; right_items?: string[]; // compare (two titled lists, side by side)
  quote?: string; attribution?: string; photo_url?: string; role?: string; // testimonial
  url?: string; alt?: string;                  // image
  embed_url?: string;                          // embed
  booking_slug?: string;                       // booking (a CRM calendar slug: /book/<slug>)
  label?: string; href?: string;               // cta
  forward_attribution?: boolean;              // explicit opt-in for this section's marketing buttons; never footer/social links
  cta_label?: string; cta_href?: string; cta2_label?: string; cta2_href?: string; // hero inline action(s): primary button + optional secondary ghost link (so the hero owns its CTA — no detached cta section)
  layout?: 'card' | 'split' | 'stacked'; form_side?: 'left' | 'right'; // form: alone, beside text or below text
  form_width?: 'standard' | 'wide' | 'full';     // card/stacked: absent preserves the original centered card
  form_ratio?: 'equal' | 'wide';               // split: wide gives the form two thirds of the row
  form_fields?: FormFieldDesign;               // page-only field sizing and layout, never shared CRM settings
  after_submit?: AfterSubmitAction;            // form: what a sent form does next. 'next' advances the funnel, 'thank_you' shows this page's thank-you, 'form' keeps the CRM form's own end screen (its thanks, not-a-fit, booking or redirect). Absent = 'next', or 'form' on the last step (see afterSubmitAction).
  stats?: StatItem[];                          // stats
  features?: FeatureItem[];                    // features
  faqs?: FaqItem[];                            // faq
  steps?: StepItem[];                          // steps
  logos?: string[];                            // logos (image URLs)
  logos_marquee?: boolean;                     // logos: continuous-scroll marquee instead of the static wrap strip (falls back to static under prefers-reduced-motion)
  links?: FooterLink[];                        // footer
  footer_layout?: 'row' | 'centered';           // absent keeps the page style's footer layout
  plans?: PricingPlan[];                       // pricing
  images?: string[];                           // gallery (image URLs)
  members?: TeamMember[];                      // team
  size?: 'sm' | 'md' | 'lg'; line?: boolean;   // divider (band height + optional hairline rule)
  deadline?: string; expired_text?: string;    // countdown (deadline = ISO datetime; video reuses video_url + heading; guarantee reuses heading + body)
  // ── v2.58 funnel fields. All optional; absent = the original render. ──
  highlight?: string;                          // words inside the main heading/headline to accent (must appear verbatim)
  mark?: MarkStyle;                            // how `highlight` renders (absent = the page style's default)
  variant?: SectionVariant;                    // layout variant (stats/faq/features/steps/benefits/testimonials/modules/pricing)
  proof_text?: string; proof_avatars?: string[]; rating?: number; // hero/cta social-proof line: avatar stack + stars + text
  proof_above?: boolean;                       // hero: show the proof line ABOVE the headline (rated-by strip) instead of under the button
  inset?: boolean;                             // modern styles: paint the band as a rounded panel inside the page margins
  cta_note?: string;                           // small reassurance line under a button (hero, cta, offer)
  poster_url?: string;                         // hero/video: a poster frame with a play button; the player loads on click (no video_url = a still placeholder)
  anchor?: string;                             // in-page anchor id (a top-bar link `#pricing` scrolls here); banded pages only
  quotes?: QuoteItem[];                        // testimonials (review wall)
  modules?: ModuleItem[];                      // modules (curriculum)
  offer_items?: OfferItem[]; price?: string; price_compare?: string; price_note?: string; // offer (value stack + price)
}

export interface PageThankYou {
  headline?: string; subhead?: string; embed_url?: string; // legacy fixed fields (upgraded by thankYouSections)
  sections?: Section[];                                    // composable thank-you body
}

/** One step of a multi-step page (a ClickFunnels-style flow inside ONE page).
 *  The visitor sees one step at a time and advances via a CTA with href '#next'
 *  or by submitting a form on a non-final step. */
export interface PageStep {
  id: string; name: string; sections: Section[];
}

export interface PageContent {
  sections?: Section[];        // legacy mirror of steps[0].sections (kept in sync by syncLegacySections)
  steps?: PageStep[];          // ordered steps; absent/empty = single-step page built from `sections`
  thank_you?: PageThankYou;
  footer?: Section;            // shared footer (type 'footer') rendered on EVERY step AND the thank-you
  // legacy fixed-slot fields (read-only; upgraded by normalizeContent)
  headline?: string; subhead?: string; hero_image_url?: string;
  benefits?: string[]; testimonial?: { quote?: string; attribution?: string }; embed_url?: string;
}

export interface PageTheme extends FormTheme {
  style?: PageStyle;
  font?: PageFont;        // display+body pairing (fonts.ts); absent = app default type
  archetype?: Archetype;  // which premium preset was applied (archetypes.ts); label only
  // Custom SEO + social-share (Open Graph) metadata. Absent fields fall back to
  // the page's hero headline/subheadline + first image (see resolvePageSeo).
  // favicon: the browser-tab icon (absent = the app's own icon).
  seo?: { title?: string; description?: string; image?: string; favicon?: string };
  form_bg_color?: string;
  form_bg_opacity?: number;
  form_mode?: 'inline' | 'popup'; // 'popup' = the form opens in a modal on CTA click (absent = inline)
  // Owner-supplied custom code (analytics pixels, verification meta, chat
  // snippets). The SERVER writes it into published pages on custom domains only:
  // head_html before </head>, body_html before </body>. It never runs on the
  // app's own hosts, in previews or in the editor, and the browser bundle never
  // injects it.
  head_html?: string;
  body_html?: string;
  /** Missing preserves existing custom snippets; false pauses without deleting them. */
  custom_scripts_enabled?: boolean;
  /** Automatically install the selected client's native Website Analytics tracker. */
  analytics_tracking?: { enabled?: boolean; site_id?: number };
  /** Button fill, when it should differ from the brand color (absent = brand).
   *  e.g. a burgundy letter with orange buttons. */
  button_color?: string;
}

/** What a landing_pages row is. Presentations (decks) are not part of NL-AOS. */
export type PageKind = 'page';

/** The CRM form a page links to (crm_forms), as the staff API returns it. */
export interface PageFormRef { id: number; slug: string; name: string; status: 'draft' | 'live' | 'archived' }

/** A landing_pages row, as the staff API (/api/pages) returns it. */
export interface LandingPage {
  id: string;
  slug: string;
  name: string;
  kind: PageKind;
  form_id: number | null;
  content: PageContent;
  theme: PageTheme;
  status: 'draft' | 'published';
  /** Unguessable capability for the /p/<slug>?pt= preview link (any status,
   *  draft included). Null until first requested (POST /api/pages/:id/preview-token). */
  preview_token: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_name?: string | null;
  updated_by_name?: string | null;
  form?: PageFormRef | null;
}

/** The fields an editor change writes (PUT /api/pages/:id) and a version snapshots. */
export type PageEdits = Pick<LandingPage, 'name' | 'slug' | 'form_id' | 'content' | 'theme'>;

/** What a visitor's browser gets (GET /api/public/pages/:slug, and inline on a
 *  server-rendered page). `form` is the linked CRM form when it is live (any
 *  status on a `?pt=` preview); `preview` is set only on a token-matched preview. */
export interface PublicPageBundle {
  id: string;
  slug: string;
  name: string;
  content: PageContent;
  theme: PageTheme;
  form: { slug: string; name: string } | null;
  preview?: true;
}

/** Resolve a page's effective SEO + social-share fields: the owner's custom
 *  theme.seo values, falling back to the first hero's headline/subheadline and the
 *  first image found in the content (a video's poster counts). Pure; shared by the
 *  editor preview, the client-only meta tags (PublicLandingPage) and the server
 *  render (public/server.tsx, which makes a relative image absolute). The
 *  favicon has no fallback: '' means the app's own icon. */
export function resolvePageSeo(content: PageContent | null | undefined, theme: PageTheme | null | undefined, fallbackTitle?: string): { title: string; description: string; image: string; favicon: string } {
  const seo = theme?.seo ?? {};
  const c = content ?? {};
  const secs: Section[] = (c.sections && c.sections.length ? c.sections : c.steps?.[0]?.sections) ?? [];
  const hero = secs.find((s) => s.type === 'hero' && (s.headline || s.subheadline));
  // An absolute image, or a path on the page's own host (template assets and
  // uploaded images are stored relative); the server makes paths absolute.
  const firstImage = secs.map((s) => s.image_url || s.url || (s.images && s.images[0]) || s.poster_url || '').find((u) => typeof u === 'string' && (/^https?:\/\//.test(u) || /^\/[^/]/.test(u))) || '';
  return {
    title: (seo.title ?? '').trim() || (hero?.headline ?? '').trim() || (fallbackTitle ?? '').trim() || 'Untitled page',
    description: (seo.description ?? '').trim() || (hero?.subheadline ?? '').trim() || '',
    image: (seo.image ?? '').trim() || (typeof firstImage === 'string' ? firstImage : ''),
    favicon: safeIconHref(seo.favicon),
  };
}

/** The icon a page uses when it has none of its own. */
export const DEFAULT_FAVICON = '/favicon.ico';

/** A favicon address a page may use: http(s), or a path on the page's own host
 *  (uploads are stored relative, so they work on every custom domain). Anything
 *  else ('javascript:', '//host', data:) is ''. */
export function safeIconHref(url: unknown): string {
  const u = typeof url === 'string' ? url.trim() : '';
  return /^https?:\/\/[^\s]+$/i.test(u) || /^\/[^/\s][^\s]*$/.test(u) ? u : '';
}

export function isHttpsUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try { return new URL(url).protocol === 'https:'; }
  catch { return false; }
}

/** Convert a shareable video URL into its embeddable iframe URL. Loom, YouTube,
 *  and Vimeo "share/watch" links don't iframe directly (they serve a full page,
 *  usually X-Frame blocked) — map them to the provider's /embed player. Anything
 *  already embeddable (Calendly, a provider /embed URL, or an unknown https URL)
 *  passes through unchanged. Pure + unit-tested. */
/** Pull the src out of a pasted <iframe …> embed code; if it's not an iframe
 *  snippet, return the trimmed input unchanged. Lets embed fields accept either a
 *  plain link OR a full embed code (Calendly/Loom/Maps "copy embed code"). */
export function embedCodeToUrl(input: string | null | undefined): string {
  const s = String(input ?? '').trim();
  const m = s.match(/<iframe[^>]*\bsrc\s*=\s*["']([^"']+)["']/i);
  if (!m) return wistiaPlayerUrl(s) ?? s;
  let src = m[1].trim();
  if (src.startsWith('//')) src = 'https:' + src;
  if (src.startsWith('http://')) src = 'https://' + src.slice('http://'.length);
  return src;
}

/** Wistia's default "Inline" embed code is scripts plus a <wistia-player> (or an older
 *  wistia_async_<id> div), not an iframe: read the video id out of it and use Wistia's
 *  own iframe player, so nothing of Wistia's runs on the page itself. Null for
 *  anything else. Pure. */
export function wistiaPlayerUrl(code: string): string | null {
  if (!/<(?:script|div|wistia-player)\b/i.test(code) || !/wistia/i.test(code)) return null;
  const m = code.match(/media-id\s*=\s*["']([a-z0-9]{6,20})["']/i)
    ?? code.match(/\bwistia_async_([a-z0-9]{6,20})\b/i)
    ?? code.match(/fast\.wistia\.(?:com|net)\/embed\/(?:medias\/)?([a-z0-9]{6,20})\.(?:jsonp|js)\b/i);
  return m ? `https://fast.wistia.net/embed/iframe/${m[1].toLowerCase()}` : null;
}

/** True for pasted embed code that has to run its own scripts (a widget's <script>
 *  snippet) and has no link or iframe to use instead. Those run in a sealed frame
 *  (EmbedBlock's ScriptEmbed). Pure. */
export function isScriptEmbed(input: string | null | undefined): boolean {
  const s = String(input ?? '').trim();
  return /<script\b/i.test(s) && !isHttpsUrl(embedCodeToUrl(s));
}

export function normalizeEmbedUrl(url: string | null | undefined): string {
  const raw = embedCodeToUrl(url);   // accept a pasted <iframe> embed code, not just a link
  if (!raw) return '';
  let u: URL;
  try { u = new URL(raw); } catch { return raw; }
  const host = u.hostname.replace(/^www\./, '');
  if (host === 'loom.com') {
    const m = u.pathname.match(/\/(?:share|embed)\/([0-9a-f]+)/i);
    if (m) return `https://www.loom.com/embed/${m[1]}`;
  }
  if (host === 'youtube.com') {
    const v = u.searchParams.get('v');
    if (v) return `https://www.youtube.com/embed/${v}`;
    const m = u.pathname.match(/\/(?:embed|shorts)\/([\w-]+)/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
  }
  if (host === 'youtu.be') {
    const id = u.pathname.slice(1).split('/')[0];
    if (id) return `https://www.youtube.com/embed/${id}`;
  }
  if (host === 'vimeo.com') {
    const m = u.pathname.match(/\/(\d+)/);
    if (m) return `https://player.vimeo.com/video/${m[1]}`;
  }
  // Wistia: <account>.wistia.com/medias/<id> (or wistia.com/medias/<id>) -> fast.wistia.net player
  if (host === 'wistia.com' || host.endsWith('.wistia.com')) {
    const m = u.pathname.match(/\/medias\/(\w+)/);
    if (m) return `https://fast.wistia.net/embed/iframe/${m[1]}`;
  }
  return raw;
}

/** Append the provider's autoplay flag to an ALREADY-normalized embed URL
 *  (YouTube/Vimeo/Loom: autoplay=1, Wistia: autoPlay=true). Used by the video
 *  poster so the player starts on the click that revealed it. Pure. */
export function addAutoplay(src: string): string {
  if (!isHttpsUrl(src)) return src;
  const flag = /fast\.wistia\.net/.test(src) ? 'autoPlay=true' : 'autoplay=1';
  return src + (src.includes('?') ? '&' : '?') + flag;
}

/** True if the (normalized) embed URL is a video player — so it should render in
 *  a responsive 16:9 box rather than a fixed-height iframe (Calendly etc.). */
export function isVideoEmbed(url: string | null | undefined): boolean {
  const u = normalizeEmbedUrl(url);
  return /(?:loom\.com\/embed\/|youtube\.com\/embed\/|player\.vimeo\.com\/video\/|fast\.wistia\.net\/embed\/)/i.test(u);
}

/** A CRM booking page (/book/<calendar>) renders transparent + chromeless when
 *  ?embed=1 is set. Append it to a /book/ URL (idempotent); leave other URLs
 *  untouched so videos/Calendly/etc. are unaffected. Pure + unit-tested. */
export function withEmbedParam(url: string | null | undefined): string {
  const u = String(url ?? '');
  if (!/\/book\//.test(u) || /[?&]embed=1(?:&|$)/.test(u)) return u;
  return u + (u.includes('?') ? '&' : '?') + 'embed=1';
}

/** Booking-embed URL for a CRM calendar slug, in transparent embed mode. Empty
 *  when no slug. Pages pass origin '' so the frame stays on the visitor's own
 *  host (the app host or a custom domain, which both serve /book/*). */
export function bookingEmbedUrl(slug: string | null | undefined, origin: string): string {
  const s = String(slug ?? '').trim();
  return s ? `${origin}/book/${encodeURIComponent(s)}?embed=1` : '';
}

export function newSection(type: SectionType, id: string): Section {
  switch (type) {
    case 'hero': return { id, type, logo: true, eyebrow: '', headline: '', subheadline: '', image_url: '', image_pos: 'side', video_url: '' };
    case 'text': return { id, type, heading: '', body: '' };
    case 'benefits': return { id, type, items: [] };
    case 'testimonial': return { id, type, quote: '', attribution: '', photo_url: '', role: '' };
    case 'image': return { id, type, url: '', alt: '' };
    case 'embed': return { id, type, embed_url: '' };
    case 'booking': return { id, type, booking_slug: '' };
    case 'cta': return { id, type, label: '', href: '#form' };
    case 'form': return { id, type, form_width: 'wide' };
    case 'stats': return { id, type, stats: [] };
    case 'features': return { id, type, features: [] };
    case 'faq': return { id, type, faqs: [] };
    case 'steps': return { id, type, steps: [] };
    case 'logos': return { id, type, logos: [] };
    case 'footer': return { id, type, body: '', links: [] };
    case 'pricing': return { id, type, plans: [] };
    case 'video': return { id, type, heading: '', video_url: '' };
    case 'gallery': return { id, type, images: [] };
    case 'team': return { id, type, members: [] };
    case 'guarantee': return { id, type, heading: '', body: '' };
    case 'divider': return { id, type, size: 'md', line: false };
    case 'countdown': return { id, type, deadline: '', heading: '', expired_text: '' };
    case 'split': return { id, type, image_url: '', image_side: 'left', eyebrow: '', heading: '', body: '', label: '', href: '' };
    case 'compare': return { id, type, left_title: 'The old way', left_items: ['Slow', 'Costly', 'Manual'], right_title: 'The new way', right_items: ['Fast', 'Simple', 'Automatic'] };
    case 'nav': return { id, type, logo: true, heading: '', links: [], label: '', href: '#form' };
    case 'banner': return { id, type, body: '', label: '', href: '' };
    case 'testimonials': return { id, type, quotes: [] };
    case 'modules': return { id, type, modules: [] };
    case 'bio': return { id, type, photo_url: '', image_side: 'left', eyebrow: '', heading: '', body: '', items: [] };
    case 'offer': return { id, type, heading: '', offer_items: [], price: '', price_compare: '', label: '', href: '#form' };
  }
}

// Neutral placeholder media for freshly-added sections so a dropped block shows
// something real (not a blank box) and is obviously editable. Template assets
// are served by the app at /template-assets/* on every host (custom domains
// included), so the paths stay relative; localizeAssets() makes them absolute
// when a page is created from a template.
export const TEMPLATE_ASSETS = '/template-assets';
const TPL = TEMPLATE_ASSETS;
const PH_WIDE = `${TPL}/abstract-wide-light.webp`;
const PH_SQ = [`${TPL}/square-1.webp`, `${TPL}/square-2.webp`, `${TPL}/square-3.webp`];
// Self-contained grey wordmark placeholders for the logo strip (no external dep,
// no broken images) — the user swaps them for real logos.
const phLogo = (label: string) => 'data:image/svg+xml,' + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="44"><rect width="160" height="44" rx="8" fill="#e5e7eb"/><text x="80" y="28" font-family="system-ui,sans-serif" font-size="17" font-weight="700" fill="#9ca3af" text-anchor="middle">${label}</text></svg>`);
const PH_LOGOS = ['Acme', 'Globex', 'Hooli', 'Initech', 'Umbrella'].map(phLogo);
// People placeholders for the review wall + bio blocks (self-hosted stock portraits).
const PH_AVATARS = [`${TPL}/v2/avatar-01.webp`, `${TPL}/v2/avatar-02.webp`, `${TPL}/v2/avatar-03.webp`];
const PH_PORTRAIT = `${TPL}/v2/portrait-01.webp`;

/** A new section pre-filled with editable PLACEHOLDER content, so dragging a
 *  component onto the canvas drops a real-looking block (headline, sample items,
 *  cards, etc.) instead of an empty one. Used by the editor's add/drop paths;
 *  templates and legacy upgrades keep using the empty newSection(). */
export function newSectionFilled(type: SectionType, id: string): Section {
  const s = newSection(type, id);
  switch (type) {
    case 'hero': return { ...s, eyebrow: 'New here', headline: 'A headline that sells the outcome', subheadline: 'One clear sentence on what you offer and who it is for.' };
    case 'text': return { ...s, heading: 'Section heading', body: 'Write a short paragraph here. Say what matters and why it helps the reader.' };
    case 'benefits': return { ...s, items: ['First benefit they get', 'Second benefit they get', 'Third benefit they get'] };
    case 'testimonial': return { ...s, quote: 'This is exactly what we needed. It paid for itself in the first month.', attribution: 'Happy Customer', role: 'Title, Company' };
    case 'features': return { ...s, heading: 'What you get', features: [
      { icon_name: 'bolt', title: 'Fast', body: 'A short line about this feature.' },
      { icon_name: 'shield', title: 'Secure', body: 'A short line about this feature.' },
      { icon_name: 'chart', title: 'Proven', body: 'A short line about this feature.' },
    ] };
    case 'stats': return { ...s, stats: [{ value: '10k+', label: 'happy customers' }, { value: '4.9/5', label: 'average rating' }, { value: '24/7', label: 'support' }] };
    case 'steps': return { ...s, heading: 'How it works', steps: [{ label: 'Step one', detail: 'What happens first.' }, { label: 'Step two', detail: 'What happens next.' }, { label: 'Step three', detail: 'The result they get.' }] };
    case 'faq': return { ...s, heading: 'Questions, answered', faqs: [{ q: 'A common question?', a: 'A clear, helpful answer.' }, { q: 'Another question?', a: 'A clear, helpful answer.' }] };
    case 'pricing': return { ...s, heading: 'Simple pricing', plans: [
      { name: 'Basic', price: '$19', period: '/mo', features: ['Feature one', 'Feature two'], cta_label: 'Choose Basic', cta_href: '#form' },
      { name: 'Pro', price: '$49', period: '/mo', features: ['Everything in Basic', 'Feature three', 'Feature four'], cta_label: 'Choose Pro', cta_href: '#form', highlight: true },
    ] };
    case 'cta': return { ...s, label: 'Get started', href: '#form' };
    case 'guarantee': return { ...s, heading: 'Our guarantee', body: 'Try it risk-free for 30 days. If it is not for you, we refund every cent.' };
    case 'team': return { ...s, heading: 'Meet the team', members: [{ name: 'Alex Doe', role: 'Founder' }, { name: 'Sam Lee', role: 'Lead' }, { name: 'Jordan Kim', role: 'Support' }] };
    case 'split': return { ...s, eyebrow: 'New', heading: 'A headline beside an image', body: 'Pair a visual with a short, persuasive paragraph that backs up the heading.', label: 'Learn more', href: '#form', image_url: PH_WIDE };
    case 'image': return { ...s, url: PH_WIDE, alt: 'Placeholder image' };
    case 'gallery': return { ...s, heading: 'Gallery', images: [...PH_SQ] };
    case 'logos': return { ...s, heading: 'Trusted by teams everywhere', logos: [...PH_LOGOS] };
    case 'video': return { ...s, heading: 'Watch the 2-minute demo' };
    case 'countdown': {
      const d = new Date(Date.now() + 7 * 86_400_000);
      const pad = (n: number) => String(n).padStart(2, '0');
      const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      return { ...s, heading: 'Offer ends in', deadline: local, expired_text: 'This offer has ended.' };
    }
    case 'nav': return { ...s, heading: 'Your brand', links: [{ label: 'How it works', href: '#' }, { label: 'Reviews', href: '#' }, { label: 'FAQ', href: '#' }], label: 'Get started', href: '#form' };
    case 'banner': return { ...s, body: 'Doors close Friday. Enrollment is open now.', label: 'Join today', href: '#form' };
    case 'testimonials': return { ...s, heading: 'What people say', quotes: [
      { quote: 'Clear, practical, and worth every minute. I used it the same week.', name: 'Jordan Ellis', role: 'Founder', photo_url: PH_AVATARS[0], rating: 5 },
      { quote: 'The first program I actually finished. The structure made it easy.', name: 'Priya Shah', role: 'Consultant', photo_url: PH_AVATARS[1], rating: 5 },
      { quote: 'I got more out of the first module than a year of free videos.', name: 'Marcus Lee', role: 'Designer', photo_url: PH_AVATARS[2], rating: 5 },
    ] };
    case 'modules': return { ...s, heading: 'What you will learn', modules: [
      { label: 'Module 1', title: 'The foundation', body: 'What to set up first, and why it matters.', lessons: ['Lesson one', 'Lesson two'] },
      { label: 'Module 2', title: 'The system', body: 'The repeatable process, step by step.', lessons: ['Lesson one', 'Lesson two'] },
      { label: 'Module 3', title: 'The results', body: 'How to measure it and keep it going.', lessons: ['Lesson one', 'Lesson two'] },
    ] };
    case 'bio': return { ...s, eyebrow: 'Meet your guide', heading: 'Hi, I am Alex.', body: 'A short story about who you are, what you have done, and why you are the right person to teach this.', items: ['10 years in the field', 'Worked with 500+ clients'], photo_url: PH_PORTRAIT };
    case 'offer': return { ...s, heading: 'Everything you get', offer_items: [
      { label: 'The full program', value: '$497 value' },
      { label: 'Templates and worksheets', value: '$97 value' },
      { label: 'Private community', value: '$197 value' },
    ], price: '$197', price_compare: '$791', label: 'Get instant access', href: '#form', cta_note: '30-day money-back guarantee' };
    default: return s; // form, embed, booking, divider, footer, compare (already realistic via newSection)
  }
}

/** Deep copy of `value` with every string that starts with '/template-assets/'
 *  turned into `${origin}/template-assets/...`. The gallery runs it on a
 *  template's content and theme when it creates a page, so images are absolute
 *  in the saved page (og:image, https checks) and keep working wherever the page
 *  is served. Anything else is copied as is. Pure. */
export function localizeAssets<T>(value: T, origin: string): T {
  const base = String(origin ?? '').replace(/\/+$/, '');
  const walk = (v: unknown): unknown => {
    if (typeof v === 'string') return v.startsWith(`${TEMPLATE_ASSETS}/`) ? `${base}${v}` : v;
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) out[k] = walk(x);
      return out;
    }
    return v;
  };
  return walk(value) as T;
}

/** Sections to render on the post-submit thank-you view. Uses the custom
 *  thank_you.sections when present; otherwise synthesizes a styled hero (+ embed)
 *  from the legacy headline/subhead/embed_url fields so older pages still render —
 *  now with the full page styling. Always returns at least a headline hero. */
export function thankYouSections(ty: PageThankYou | undefined, mkId: (n: number) => string): Section[] {
  const t = ty ?? {};
  if (Array.isArray(t.sections) && t.sections.length > 0) return t.sections;
  let n = 0;
  const out: Section[] = [{
    id: mkId(n++), type: 'hero', logo: false,
    headline: (t.headline ?? '').trim() || 'Thanks, we got it.',
    subheadline: (t.subhead ?? '').trim() || "We'll be in touch shortly.",
  }];
  if (t.embed_url) out.push({ id: mkId(n++), type: 'embed', embed_url: t.embed_url });
  return out;
}

/** Deep-copy a section, giving the copy a fresh id. Used by the editor's Duplicate. */
export function duplicateSection(section: Section, newId: string): Section {
  return { ...structuredClone(section), id: newId };
}

/** Pure array move: returns a new array with the item at `from` moved to `to`.
 *  Out-of-bounds indices return the array unchanged. */
export function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from < 0 || from >= arr.length || to < 0 || to >= arr.length || from === to) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function emptyPage(mkId: (n: number) => string): { sections: Section[]; thank_you: PageThankYou } {
  return {
    sections: [newSection('hero', mkId(0)), newSection('form', mkId(1))],
    thank_you: { headline: '', subhead: '', embed_url: '' },
  };
}

export function normalizeContent(
  content: PageContent | null | undefined,
  mkId: (n: number) => string,
): { sections: Section[]; thank_you: PageThankYou; steps: PageStep[] } {
  const c = content ?? {};
  const thank_you = c.thank_you ?? {};
  // Steps upgrade: a page without steps is a single-step page whose only step
  // holds the (normalized) sections. Existing steps pass through untouched.
  const withSteps = (sections: Section[]) => ({
    sections, thank_you,
    steps: Array.isArray(c.steps) && c.steps.length > 0
      ? c.steps
      : [{ id: 'step_1', name: 'Step 1', sections }],
  });
  if (Array.isArray(c.sections) && c.sections.length > 0) return withSteps(c.sections);
  const sections: Section[] = [];
  let n = 0;
  sections.push({
    id: mkId(n++), type: 'hero', logo: true,
    headline: c.headline ?? '', subheadline: c.subhead ?? '', image_url: c.hero_image_url ?? '',
  });
  if (Array.isArray(c.benefits) && c.benefits.length > 0)
    sections.push({ id: mkId(n++), type: 'benefits', items: c.benefits });
  if (c.testimonial?.quote)
    sections.push({ id: mkId(n++), type: 'testimonial', quote: c.testimonial.quote, attribution: c.testimonial.attribution });
  sections.push({ id: mkId(n++), type: 'form' });
  if (c.embed_url)
    sections.push({ id: mkId(n++), type: 'embed', embed_url: c.embed_url });
  return withSteps(sections);
}

/** Normalized accessor for a page's steps: existing steps pass through; a
 *  step-less page becomes one step wrapping its (normalized) sections. Always
 *  returns at least one step. Pure. */
export function pageSteps(content: PageContent | null | undefined): PageStep[] {
  return normalizeContent(content, (n) => `s_${n}`).steps;
}

/** A booking section with no calendar picked yet: the editor shows a
 *  placeholder, visitors see nothing (no empty band). Pure. */
export function bookingNeedsCalendar(section: Section): boolean {
  return section.type === 'booking' && !(section.booking_slug ?? '').trim();
}

/** What a visitor sees of a section list: hidden sections and booking
 *  sections without a calendar are left out. Pure (server and browser agree). */
export function visitorSections(sections: Section[]): Section[] {
  return sections.some((x) => x.hidden || bookingNeedsCalendar(x)) ? sections.filter((x) => !x.hidden && !bookingNeedsCalendar(x)) : sections;
}

/** True when a visible booking section anywhere on the page (any step or the
 *  thank-you) still has no calendar. Pure. */
export function pageNeedsCalendar(content: PageContent | null | undefined): boolean {
  const all = [...pageSteps(content).flatMap((st) => st.sections), ...(content?.thank_you?.sections ?? [])];
  return all.some((x) => !x.hidden && bookingNeedsCalendar(x));
}

/** True when a visible Form section sits on the page (any step or the
 *  thank-you) but no CRM form is linked: visitors would see no form. Pure. */
export function pageNeedsForm(content: PageContent | null | undefined, formId: number | null | undefined): boolean {
  if (formId) return false;
  const all = [...pageSteps(content).flatMap((st) => st.sections), ...(content?.thank_you?.sections ?? [])];
  return all.some((x) => !x.hidden && x.type === 'form');
}

// ── Deadlines (countdown, announcement bar) ─────────────────────────────
// Typed and shown in US Eastern (the agency's clock), stored as an ISO time
// with the Eastern offset of that day, so every visitor's timer ends at the
// same moment wherever they are.
const ET_TZ = 'America/New_York';
const HAS_ZONE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

/** Minutes east of UTC that Eastern time is at this instant (-240 or -300). */
function etOffsetMinutes(at: number): number {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: ET_TZ, timeZoneName: 'longOffset' })
    .formatToParts(new Date(at)).find((p) => p.type === 'timeZoneName')?.value ?? '';
  const m = /GMT([+-])(\d{2}):?(\d{2})?/.exec(name);
  if (!m) return -300;
  return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0));
}

/** "2026-10-20T23:59" read as Eastern wall time -> "2026-10-20T23:59:00-04:00"
 *  (daylight time handled). '' for anything else. Pure. */
export function etWallToIso(wall: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wall ?? '');
  if (!m) return '';
  const asUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  // The offset of the real instant (one correction is enough around the switch).
  const off = etOffsetMinutes(asUtc - etOffsetMinutes(asUtc) * 60_000);
  const abs = Math.abs(off);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:00${off < 0 ? '-' : '+'}${hh}:${mm}`;
}

/** A stored deadline as the Eastern wall time a datetime-local field shows. A
 *  value without a zone is shown as typed. Pure. */
export function isoToEtWall(value: string | null | undefined): string {
  if (!value) return '';
  if (!HAS_ZONE.test(value)) return value.slice(0, 16);
  const t = Date.parse(value);
  if (Number.isNaN(t)) return '';
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: ET_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(t)).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/** The moment a deadline ends, in ms (NaN when unset). A value without a zone
 *  (typed before deadlines were stored with one) counts as Eastern time. Pure. */
export function deadlineTime(value: string | null | undefined): number {
  if (!value) return NaN;
  return Date.parse(HAS_ZONE.test(value) ? value : etWallToIso(value) || value);
}

/** The step that follows `stepId`, or null when it is the last step (or the id
 *  is unknown). Pure. */
export function stepAfter(content: PageContent | null | undefined, stepId: string): PageStep | null {
  const steps = pageSteps(content);
  const i = steps.findIndex((s) => s.id === stepId);
  return i >= 0 && i + 1 < steps.length ? steps[i + 1] : null;
}

/** Mirror steps[0].sections onto content.sections (and guarantee steps exist)
 *  so everything that reads content.sections — old published bundles, list
 *  thumbnails, save-as-template, duplicate, assistant-built pages — keeps
 *  working. Call on every save. Pure. */
export function syncLegacySections(content: PageContent): PageContent {
  const steps = pageSteps(content);
  return { ...content, steps, sections: steps[0]?.sections ?? [] };
}

/** A fresh step with a sensible default body (hero + a "Continue" CTA wired to
 *  '#next') so a newly added step advances the flow out of the box. */
export function newStep(id: string, name: string, mkId: () => string): PageStep {
  const cta = newSection('cta', mkId());
  cta.label = 'Continue';
  cta.href = '#next';
  return { id, name, sections: [newSection('hero', mkId()), cta] };
}

/** What a '#next' CTA should do from step `currentIdx`: advance to the next
 *  step; on the last step fall back to scrolling to the form (when the step has
 *  one), else do nothing. Pure — the caller performs the actual action. */
export type NextStepTarget = { kind: 'step'; index: number } | { kind: 'form' } | { kind: 'none' };
export function nextStepTarget(steps: PageStep[], currentIdx: number): NextStepTarget {
  if (currentIdx >= 0 && currentIdx < steps.length - 1) return { kind: 'step', index: currentIdx + 1 };
  const cur = steps[currentIdx];
  return cur?.sections.some((s) => s.type === 'form') ? { kind: 'form' } : { kind: 'none' };
}

/** What a form section's successful submit should do, given whether its step is
 *  the funnel's last step. An explicit 'thank_you' or 'form' (keep the CRM
 *  form's own end screen) always wins. 'next' or no choice: the next step, and
 *  on the last step the page's thank-you, as in Orbit. Pure; the caller
 *  performs the action. */
export function afterSubmitAction(section: Section | undefined, isLastStep: boolean): AfterSubmitAction {
  const v = section?.after_submit;
  if (v === 'thank_you' || v === 'form') return v;
  return isLastStep ? 'thank_you' : 'next';
}

/** Parse a `?step=` URL param (1-based) into a 0-based step index. Anything
 *  invalid or out of range lands on the first step. Pure. */
export function parseStepParam(raw: string | null | undefined, stepCount: number): number {
  const n = Number(raw);
  if (!Number.isInteger(n)) return 0;
  return n >= 1 && n <= stepCount ? n - 1 : 0;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return { r: 255, g: 255, b: 255 };
  const int = parseInt(m[1], 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

/** Returns black or white — whichever is more legible as text/icon on top of `hex`.
 *  Uses relative luminance so it works for any user-picked brand color. */
export function readableOn(hex: string | undefined): string {
  const { r, g, b } = hexToRgb(hex ?? '#000000');
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#0b0e0c' : '#ffffff';
}

/** The accent a CRM form takes on this page (its buttons, progress bar and
 *  selected answers): the page's button color, else its brand color, as long as
 *  it shows on the form's white card (a near-white color falls through to the
 *  next, then to near-black). The form picks dark or white text for its buttons
 *  itself (formInkOn), so a gold or yellow accent keeps its color. Used for the
 *  iframe's ?accent= and for the form a template creates. Pure. */
export function formAccentFor(theme: { button_color?: string; brand_color?: string } | null | undefined): string {
  for (const c of [theme?.button_color, theme?.brand_color || '#4f46e5']) {
    if (c && /^#[0-9a-f]{6}$/i.test(c) && luminance(c) <= 0.85) return c;
  }
  return '#0b0e0c';
}

/** The text color for a form button filled with `accent`: near-black on a light
 *  fill, white on a dark one (the same cut as readableOn). Pure. */
export function formInkOn(accent: string): string {
  return readableOn(accent);
}

function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export function formBgCss(theme: { form_bg_color?: string; form_bg_opacity?: number }): string {
  const color = theme.form_bg_color ?? '#ffffff';
  const op = theme.form_bg_opacity ?? 100;
  if (op <= 0) return 'transparent';
  const { r, g, b } = hexToRgb(color);
  const a = Math.max(0, Math.min(100, op)) / 100;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// Page form-fill conversion rate: submissions / views, as a 0-100 integer percent.
// Divide-by-zero safe (0 views → 0%). Caps at 100 in case of stale/duplicate counts.
export function pageConversionRate(submissions: number, views: number): number {
  if (!views || views <= 0) return 0;
  return Math.min(100, Math.round((submissions / views) * 100));
}
